// @flow
import axios from './defaultClient';

// SSO 설정
const SSO_CONFIG = {
  baseUrl: process.env.REACT_APP_SSO_BASE_URL || 'http://localhost:6001',
  clientId: process.env.REACT_APP_SSO_CLIENT_ID || 'cacheby-web',
  redirectUri: process.env.REACT_APP_SSO_REDIRECT_URI || 'http://localhost:3000/callback',
  scopes: 'openid profile email',
};

/**
 * OAuth2 Authorization Code Flow URL 생성
 * @param {boolean} isSignup - 회원가입 여부
 * @returns {string} SSO 인증 URL
 */
export const generateAuthUrl = (isSignup: boolean = false): string => {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: SSO_CONFIG.clientId,
    redirect_uri: SSO_CONFIG.redirectUri,
    scope: SSO_CONFIG.scopes,
    state: JSON.stringify({
      timestamp: Date.now(),
      action: isSignup ? 'signup' : 'login',
      returnUrl: window.location.pathname,
    }),
  });

  const endpoint = isSignup ? '/auth/signup' : '/auth/login';
  return `${SSO_CONFIG.baseUrl}${endpoint}?${params.toString()}`;
};

/**
 * SSO 로그인 페이지로 리다이렉트
 */
export const redirectToLogin = (): void => {
  window.location.href = generateAuthUrl(false);
};

/**
 * SSO 회원가입 페이지로 리다이렉트
 */
export const redirectToSignup = (): void => {
  window.location.href = generateAuthUrl(true);
};

/**
 * Authorization Code를 Access Token으로 교환 (백엔드를 통해)
 * @param {string} code - Authorization Code
 * @param {string} state - State 파라미터
 * @returns {Promise<Object>} 토큰 및 사용자 정보
 */
export const exchangeCodeForToken = async (code: string, state: string): Promise<*> => {
  const response = await axios.post('/auth/sso/token', {
    code,
    state,
  });

  return response.data;
};

/**
 * 사용자 정보 조회 (백엔드를 통해)
 * @returns {Promise<Object>} 사용자 정보
 */
export const getUserInfo = async (): Promise<*> => {
  const response = await axios.get('/auth/sso/check');
  return response.data;
};

/**
 * SSO 세션 확인 및 자동 로그인
 * @returns {Promise<Object>} { authenticated: boolean, code?: string, state?: string }
 */
export const checkSSOSession = async (): Promise<*> => {
  try {
    const authUrl = generateAuthUrl(false);
    const url = new URL(authUrl);
    const clientId = url.searchParams.get('client_id');
    const redirectUri = url.searchParams.get('redirect_uri');

    const response = await fetch(
      `${SSO_CONFIG.baseUrl}/auth/check-session?client_id=${clientId}&redirect_uri=${encodeURIComponent(
        redirectUri || ''
      )}`,
      {
        credentials: 'include',
      }
    );

    if (!response.ok) {
      return { authenticated: false };
    }

    return await response.json();
  } catch (e) {
    console.warn('SSO session check failed:', e);
    return { authenticated: false };
  }
};

/**
 * 로그아웃 (토큰 삭제 + 백엔드 로그아웃 + SSO 서버 로그아웃)
 */
export const logout = async (): Promise<void> => {
  // 백엔드 로그아웃 API 호출 (쿠키 삭제)
  try {
    const response = await axios.post('/auth/sso/logout');
    const { url } = response.data;

    // SSO 서버 로그아웃 페이지로 리다이렉트
    window.location.href = url;
  } catch (e) {
    console.warn('SSO logout failed:', e);
    // 실패해도 로컬 로그아웃은 진행
    window.location.href = '/';
  }
};

/**
 * SSO 프로필 설정 페이지 URL 반환
 * @returns {string} SSO 프로필 설정 URL
 */
export const getSSOProfileUrl = (): string => {
  return `${SSO_CONFIG.baseUrl}/auth/profile`;
};

export default {
  generateAuthUrl,
  redirectToLogin,
  redirectToSignup,
  exchangeCodeForToken,
  getUserInfo,
  checkSSOSession,
  logout,
  getSSOProfileUrl,
  SSO_CONFIG,
};



// WEBPACK FOOTER //
// ./src/lib/sso.js